import { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import {
  ArrowUturnLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Venue, VenueEvent } from 'types/model'

import { H1, H2 } from '@elements/headings'
import { Button } from '@elements/button'
import {
  getDateFromNow,
  isVenueEventPassedByInstant,
  withDateFormat,
} from '@utils/date-time'
import { EventCalendarDesktopTable } from './event-calendar-desktop-table'
import { useScrollAnchor } from '@hooks/scroll-anchor'
import { EventListView } from './event-list-view'
import { Breakpoint } from '@elements/breakpoint'

export interface EventCalendarViewProps {
  stickyHeader?: boolean
  title?: string
  venue: Venue
  events?: VenueEvent[]
  month: number
  year: number
  loading?: boolean
  onMonthChange: (date: { year: number; month: number }) => void
  showMobileBottomPaginator?: boolean
  onCellClick: (event: VenueEvent) => void
}

export function EventCalendarView({
  stickyHeader = true,
  title,
  venue,
  events,
  onMonthChange,
  month,
  year,
  loading = false,
  showMobileBottomPaginator = true,
  onCellClick,
}: EventCalendarViewProps) {
  const { setScrollAnchor, scrollToAnchor } = useScrollAnchor()
  const selectedMonthDate = new Date(year, month)

  const handleMonthChange = useCallback(
    ({ year, month }: { year: number; month: number }) => {
      onMonthChange({ year, month })
      scrollToAnchor()
    },
    [onMonthChange, scrollToAnchor]
  )

  const mobileEvents = useMemo(() => {
    const nowMs = Date.now()
    const venueTimezone = venue.location.timezone
    return events?.filter(
      (event) =>
        !isVenueEventPassedByInstant(
          nowMs,
          event.dateStart,
          event.dateEnd,
          { timezone: event.timezone || venueTimezone }
        )
    )
  }, [events, venue.location.timezone])

  return (
    <div
      className='relative flex flex-col items-start justify-start w-full'
      ref={setScrollAnchor}
    >
      <div
        className={classNames(
          'py-3 flex flex-col items-start justify-between w-full md:items-center md:space-x-4 md:flex-row',
          { 'sticky top-0 z-[1] background-basic': stickyHeader }
        )}
      >
        {title && (
          <>
            <H1 className='heading-2xl'>{title}</H1>
            <span className='hidden md:inline-block heading-2xl'> - </span>
          </>
        )}
        <div className='flex items-center justify-between w-full'>
          <H2 className='heading-lg md:heading-2xl'>
            {withDateFormat(selectedMonthDate, 'LLL. yyyy')}
          </H2>
          <EventCalendarPaginator
            month={month}
            year={year}
            onMonthChange={handleMonthChange}
          />
        </div>
      </div>

      <Breakpoint.MD>
        <EventCalendarDesktopTable
          onCellClick={onCellClick}
          stickyHeader={stickyHeader}
          events={events ?? []}
          venue={venue}
          year={year}
          month={month}
          loading={loading}
        />
      </Breakpoint.MD>

      <Breakpoint.MD max>
        <div className='w-full mt-6'>
          <EventListView
            venue={venue}
            events={mobileEvents}
            loading={loading}
            onItemClick={onCellClick}
          />
          {!!events?.length && showMobileBottomPaginator && (
            <div className='mt-6 md:hidden'>
              <EventCalendarPaginator
                month={month}
                year={year}
                onMonthChange={handleMonthChange}
              />
            </div>
          )}
        </div>
      </Breakpoint.MD>
    </div>
  )
}

function EventCalendarPaginator({
  onMonthChange,
  month,
  year,
}: Pick<EventCalendarViewProps, 'onMonthChange' | 'month' | 'year'>) {
  const selectedMonthDate = new Date(year, month)
  const todaysDate = getDateFromNow()

  const handleMonthNext = useCallback(() => {
    const isLastMonth = month === 11
    onMonthChange({
      month: isLastMonth ? 0 : month + 1,
      year: isLastMonth ? year + 1 : year,
    })
  }, [month, onMonthChange, year])

  const handleMonthPrev = useCallback(() => {
    const isFirstMonth = month === 0
    onMonthChange({
      month: isFirstMonth ? 11 : month - 1,
      year: isFirstMonth ? year - 1 : year,
    })
  }, [month, onMonthChange, year])

  const handleJumpToToday = useCallback(() => {
    onMonthChange({
      month: todaysDate.getMonth(),
      year: todaysDate.getFullYear(),
    })
  }, [onMonthChange, todaysDate])

  return (
    <div className='flex items-center justify-end space-x-3'>
      {(todaysDate.getMonth() !== month ||
        todaysDate.getFullYear() !== year) && (
        <Button.Floating
          sm
          leftIcon={ArrowUturnLeftIcon}
          onClick={handleJumpToToday}
        >
          This Month
        </Button.Floating>
      )}
      {(selectedMonthDate.getFullYear() > todaysDate.getFullYear() ||
        selectedMonthDate.getMonth() > todaysDate.getMonth()) && (
        <Button.Tertiary sm onClick={handleMonthPrev}>
          <ChevronLeftIcon className='w-4 h-4 shrink-0' />
        </Button.Tertiary>
      )}
      <Button.Tertiary sm onClick={handleMonthNext}>
        <ChevronRightIcon className='w-4 h-4 shrink-0' />
      </Button.Tertiary>
    </div>
  )
}
